import { useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Slide } from '@rossum/ui/material';
import {
  useDocumentStore,
  useDocumentStoreContext,
} from '../document-store/DocumentStore';
import { visiblePageZustandSelector } from '../document-store/documentStoreSelectors';
import { useDetectedSourceLanguage } from './hooks/useDetectedSourceLanguage';
import { useTranslationControls } from './hooks/useTranslationControls';
import { useTranslationEnabled } from './hooks/useTranslationEnabled';
import { TranslateLanguage, TranslateSourceLanguage } from './languages';
import { TranslationPanel } from './TranslationPanel';

type ActiveTranslationPanelProps = {
  sourceLanguage: TranslateSourceLanguage;
  targetLanguage: TranslateLanguage;
  profileLanguage: TranslateLanguage | undefined;
  onSourceLanguageChange: (language: TranslateSourceLanguage) => void;
  onTargetLanguageChange: (language: TranslateLanguage) => void;
  onClose: () => void;
};

/**
 * Split out so the store subscription and the detection observer only exist
 * while translation is actually running.
 */
const ActiveTranslationPanel = ({
  sourceLanguage,
  targetLanguage,
  profileLanguage,
  onSourceLanguageChange,
  onTargetLanguageChange,
  onClose,
}: ActiveTranslationPanelProps) => {
  const { dimensions } = useDocumentStoreContext();
  const { pageNumber } = useDocumentStore(
    useShallow(visiblePageZustandSelector(dimensions))
  );

  const detectedSourceLanguage = useDetectedSourceLanguage({
    targetLanguage,
    sourceLanguage,
    pageNumber,
  });

  return (
    <TranslationPanel
      sourceLanguage={sourceLanguage}
      targetLanguage={targetLanguage}
      detectedSourceLanguage={detectedSourceLanguage}
      profileLanguage={profileLanguage}
      onSourceLanguageChange={onSourceLanguageChange}
      onTargetLanguageChange={onTargetLanguageChange}
      onClose={onClose}
    />
  );
};

export const DocumentTranslationPanel = () => {
  const isTranslationEnabled = useTranslationEnabled();

  const {
    isActive,
    sourceLanguage,
    targetLanguage,
    profileLanguage,
    turnOff,
    setSourceLanguage,
    setTargetLanguage,
  } = useTranslationControls();

  const lastActiveLanguages = useRef<{
    sourceLanguage: TranslateSourceLanguage;
    targetLanguage: TranslateLanguage;
    profileLanguage: TranslateLanguage | undefined;
  } | null>(null);

  if (targetLanguage) {
    lastActiveLanguages.current = {
      sourceLanguage,
      targetLanguage,
      profileLanguage,
    };
  }

  if (!isTranslationEnabled || !lastActiveLanguages.current) return null;

  return (
    <Slide in={isActive} mountOnEnter unmountOnExit>
      <div>
        <ActiveTranslationPanel
          {...lastActiveLanguages.current}
          onSourceLanguageChange={setSourceLanguage}
          onTargetLanguageChange={setTargetLanguage}
          onClose={turnOff}
        />
      </div>
    </Slide>
  );
};
